
import { tryLast, concat, tryItem, length, unzip, cons, map, max as max_1, empty, append, singleton, ofArray, choose } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { tryFind } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { fold } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { interpolate } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { min, max } from "../../fable_modules/fable-library-js.5.13.0/Double.js";
import { compareArrays, compare, comparePrimitives, int32ToString } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { zoomInSVG as zoomInSVG_1, zoomOutSVG as zoomOutSVG_1 } from "../Style.js";
import { waveTableRightGap, lineThickness, cursorColumnColor, clkLineWidth, scrollBarWidth, topHalfHeight, rightMargin, leftMargin, rowBandColor, valueColumnMaxChars, namesValuesColumnColor, borderProperties, deleteSymbolWidth, valueColumnFontFamily, labelPadding, softScrollBarWidth, viewBoxHeight, columnFontSize, columnFontFamily, topRowButtonMargin, viewSymbolWidth, rowHeight } from "./WaveSimTypes.js";
import { DOMAttr, CSSProp_Overflow_27AED5E3, SVGAttr, HTMLAttr, CSSProp } from "../../fable_modules/Fable.React.9.4.0/Fable.React.Props.fs.js";
import { keyValueList } from "../../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import * as react from "react";
import { empty as empty_1, singleton as singleton_1, append as append_1, delay, toList } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";
import { Constants_wsButtonHeight } from "../ModelHelpers.js";
import { Option } from "../../fable_modules/Fulma.3.0.0/Elements/Button.fs.js";
import { Color_IColor } from "../../fable_modules/Fulma.3.0.0/Common.fs.js";
import { changeToolTip, getWaveToolTip, evilSvgToolTip } from "./EvilHoverCache.js";
import { getTextWidthInPixels, Text$, defaultText } from "../../Common/DrawHelpers.js";
import { fromOne, op_LeftShift, op_Subtraction } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { Constants_maxBinaryDisplayWidth, fastDataToPaddedString } from "../../Simulator/NumberHelpers.js";
import { FastData, FastBits } from "../../Simulator/SimGraphTypes.js";
import { rangeDouble } from "../../fable_modules/fable-library-js.5.13.0/Range.js";
import { tryDrawn } from "./WaveDrawn.js";
import { Msg, WaveSimModel } from "../../Model/ModelType.js";
import { difference, contains, filter, union, ofList } from "../../fable_modules/fable-library-js.5.13.0/Set.js";

/**
 * The waves the viewer draws: those the user has selected that the simulation still holds. All
 * three columns are built from this, so it is also what a row number means anything against.
 */
export function selectedWaves(wsModel) {
    return choose((wi) => tryFind(wi, wsModel.WaveDetails), wsModel.SelectedWaves);
}

/**
 * Convert XYPos list to string
 */
export function pointsToString(points) {
    return fold((str, point) => (`${str} ${interpolate("%.1f%P()", [point.X])},${interpolate("%.1f%P()", [point.Y])} `), "", points);
}

export function screenHeight() {
    return document.defaultView.innerHeight;
}

export function screenWidth() {
    return document.defaultView.innerWidth;
}

/**
 * Width of one clock cycle.
 */
export function singleWaveWidth(m) {
    return max(5, m.WaveformColumnWidth / m.ShownCycles);
}

/**
 * Left-most coordinate of the SVG viewbox.
 */
export function viewBoxMinX(m) {
    return int32ToString(0);
}

/**
 * Total width of the SVG viewbox.
 */
export function viewBoxWidth(m) {
    return max(5, m.WaveformColumnWidth).toString();
}

/**
 * Right-most visible clock cycle.
 */
export function endCycle(wsModel) {
    return ((wsModel.StartCycle + wsModel.ShownCycles) - 1) | 0;
}

export const zoomOutSVG = zoomOutSVG_1;

export const zoomInSVG = zoomInSVG_1;

export function valueTopPadding(ws) {
    return ((rowHeight - ws.WSConfig.FontSize) - 8) / 2;
}

export const separatorColour = "rgb(219,219,219)";

/**
 * Background for the names column, or for one row of it.
 * The strip at the left, where the view button sits, is painted as pane rather than as column, so
 * that the button reads as standing outside the line the labels start on - which is what it is.
 * The delete cross is inside that line, because it belongs to the label.
 * Any line along the row starts at that same place rather than running out into the strip. It is
 * painted as a second background layer, since a border cannot be inset. The line is given as
 * thickness, colour, and whether it goes along the top of the element rather than the bottom.
 */
export function namesColBackground(fill, line) {
    const edge = int32ToString(viewSymbolWidth) + "px";
    const fillLayer = ((((("linear-gradient(to right, white " + edge) + ", ") + fill) + " ") + edge) + ")";
    if (line != null) {
        const atTop = line[2];
        return ofArray([new CSSProp(/* BackgroundImage */ 23, [((((((fillLayer + ", linear-gradient(to right, transparent ") + edge) + ", ") + line[1]) + " ") + edge) + ")"]), new CSSProp(/* BackgroundSize */ 29, [("100% 100%, 100% " + int32ToString(line[0])) + "px"]), new CSSProp(/* BackgroundPosition */ 25, [atTop ? "0 0, 0 0" : "0 0, 0 100%"]), new CSSProp(/* BackgroundRepeat */ 28, ["no-repeat"])]);
    }
    else {
        return singleton(new CSSProp(/* BackgroundImage */ 23, [fillLayer]));
    }
}

/**
 * Empty row used in namesColumn and valuesColumn. Shifts these down by one
 * to allow for the row of clk cycle numbers in waveformsColumn.
 * The background carries the line under the row: the names column insets that line past the view
 * button strip, the values column has no strip and draws it as a plain border.
 */
export function topRow(ws, background, topRowContent) {
    let props;
    return singleton((props = [["style", keyValueList(append(ofArray([new CSSProp(/* Height */ 189, [rowHeight]), new CSSProp(/* PaddingTop */ 281, [valueTopPadding(ws)])]), background), 1)]], react.createElement("div", keyValueList(props, 1), ...topRowContent)));
}

export const plainTopRowLine = singleton(new CSSProp(/* BorderBottom */ 42, ["2px solid " + separatorColour]));

export const errorMessageStyle = ["style", {
    width: "90%",
    marginLeft: "5%",
    marginTop: "15px",
}];

export const checkboxStyle = ["style", {
    cursor: "pointer",
    float: "left",
}];

export const checkboxInputProps = ofArray([new HTMLAttr(/* Type */ 159, ["checkbox"]), checkboxStyle]);

export const boldFontStyle = ofArray([new CSSProp(/* FontWeight */ 165, ["bold"]), new CSSProp(/* FontSize */ 153, ["14px"])]);

export const normalFontStyle = ofArray([new CSSProp(/* FontWeight */ 165, ["normal"]), new CSSProp(/* FontSize */ 153, ["14px"])]);

/**
 * Style for top row of buttons.
 * 
 * Each is as wide as its own label needs. It used to be one fixed width for a shipped build and a
 * wider one for a development build - where the button that starts a simulation also names the
 * simulator it will start - which made every OTHER button in the row that much fatter to hold a
 * label none of them has, ran the row past the edge of the pane, and left the development build
 * looking nothing like the one people use.
 */
export function topRowButtonStyle(isRightSide) {
    return ["style", keyValueList(toList(delay(() => append_1(singleton_1(new CSSProp(/* Height */ 189, [Constants_wsButtonHeight])), delay(() => append_1(singleton_1(new CSSProp(/* FontSize */ 153, ["16px"])), delay(() => append_1(singleton_1(new CSSProp(/* Flex */ 133, ["0 0 auto"])), delay(() => append_1(isRightSide ? singleton_1(new CSSProp(/* MarginLeft */ 224, ["auto"])) : singleton_1(new CSSProp(/* AlignSelf */ 2, ["flex-start"])), delay(() => append_1(singleton_1(new CSSProp(/* MarginRight */ 225, [topRowButtonMargin])), delay(() => singleton_1(new CSSProp(/* MarginLeft */ 224, [topRowButtonMargin])))))))))))))), 1)];
}

export function topHalfButtonPropsLoading(isLoading, color, buttonId, isRightSide) {
    return ofArray([new Option(/* Color */ 0, [color]), new Option(/* IsLoading */ 13, [isLoading]), new Option(/* Props */ 17, [ofArray([new HTMLAttr(/* Id */ 99, [buttonId]), topRowButtonStyle(isRightSide)])])]);
}

export const topHalfButtonProps = (color) => ((buttonId) => ((isRightSide) => topHalfButtonPropsLoading(false, color, buttonId, isRightSide)));

export function selectRamButtonProps(buttonId) {
    return topHalfButtonProps(Color_IColor.IsInfo)(buttonId)(true);
}

/**
 * Props for selectRamButton when no RAMs are selectable
 */
export function selectRamButtonPropsLight(buttonId) {
    return append(selectRamButtonProps(buttonId), singleton(Option.IsLight));
}

export const selectWavesButtonProps = topHalfButtonProps(Color_IColor.IsInfo);

/**
 * Props for selectWavesButton when no waves are selectable
 */
export function selectWavesButtonPropsLight(buttonId) {
    return append(selectWavesButtonProps(buttonId)(true), singleton(Option.IsLight));
}

export const centerAlignStyle = ["style", {
    textAlign: "center",
    fontSize: "15px",
}];

export const ramRowStyle = ["style", {
    height: rowHeight,
    borderBottom: "1px solid rgb(219,219,219)",
}];

/**
 * Style for each row of ramTable
 */
export function ramTableRowStyle(rowType) {
    switch (rowType.tag) {
        case 1:
            return ofArray([new CSSProp(/* BackgroundColor */ 21, ["hsl(206, 70%, 96%)"]), new CSSProp(/* Color */ 103, ["hsl(204, 86%, 53%)"]), new CSSProp(/* FontWeight */ 165, ["bold"])]);
        case 2:
            return empty();
        default:
            return ofArray([new CSSProp(/* BackgroundColor */ 21, ["hsl(347, 90%, 96%)"]), new CSSProp(/* Color */ 103, ["hsl(348, 100%, 61%)"]), new CSSProp(/* FontWeight */ 165, ["bold"])]);
    }
}

export const ramTableLevelProps = singleton(["style", {
    font: columnFontFamily,
    fontSize: columnFontSize,
    position: "relative",
    display: "inline-block",
    marginRight: 20,
    marginLeft: 20,
}]);

export const ramTablesLevelProps = singleton(["style", {
    overflowX: "auto",
    font: columnFontFamily,
    fontSize: columnFontSize,
}]);

/**
 * Props for displaying values on non-binary waves by starting position.
 */
export function singleValueOnWaveProps(isStart, textFont, textWeight, xpos) {
    return ofArray([new SVGAttr(/* X */ 44, [xpos]), new SVGAttr(/* Y */ 57, [(0.5 * viewBoxHeight) + (textFont / 2)]), ["style", {
        textAnchor: isStart ? "start" : "end",
        fontFamily: "Helvetica",
        fontSize: textFont,
        fontWeight: textWeight,
    }]]);
}

/**
 * SVG group element for tooltip.
 * The props of the tooltip, as well as its text, are set in the function <c>changeToolTip</c>.
 * Initial props make it invisible.
 */
export function svgWaveToolTip(ws) {
    return evilSvgToolTip("waveTip", ws, "", singleValueOnWaveProps(true, ws.WSConfig.FontSize, ws.WSConfig.FontWeight, 0));
}

export const clkCycleButtonStyle = ["style", {
    height: rowHeight,
    textAlign: "center",
    display: "inline",
    fontSize: "13px",
    whiteSpace: "nowrap",
}];

export const clkCycleInputStyle = ["style", {
    margin: "0 0 0 0",
    textAlign: "center",
    width: "80px",
    height: rowHeight,
    display: "inline",
    fontSize: "13px",
    fontWeight: 600,
    borderColor: "gray",
    borderWidth: "1px 0.5px 1px 0.5px",
    borderRadius: 0,
    whiteSpace: "nowrap",
}];

export const clkCycleInputProps = ofArray([new HTMLAttr(/* Min */ 119, [0]), new HTMLAttr(/* SpellCheck */ 148, [false]), new HTMLAttr(/* Step */ 154, [1]), clkCycleInputStyle]);

/**
 * List of Style properties for clock cycle button
 */
export function clkCycleBut(height) {
    return ofArray([new CSSProp(/* Margin */ 218, [0]), new CSSProp(/* Height */ 189, [height]), new CSSProp(/* Padding */ 273, [0]), new CSSProp(/* Width */ 395, ["30px"]), new CSSProp(/* BorderColor */ 49, ["gray"]), new CSSProp(/* BorderWidth */ 82, ["1px 0.5px 1px 0.5px"]), new CSSProp(/* WhiteSpace */ 392, ["nowrap"])]);
}

export const clkCycleInnerStyle = ["style", keyValueList(append(clkCycleBut(rowHeight), ofArray([new CSSProp(/* BorderRadius */ 69, [0]), new CSSProp(/* WhiteSpace */ 392, ["nowrap"])])), 1)];

export const clkCycleLeftStyle = ["style", keyValueList(append(clkCycleBut(rowHeight), ofArray([new CSSProp(/* BorderTopLeftRadius */ 78, ["4px"]), new CSSProp(/* BorderBottomLeftRadius */ 44, ["4px"]), new CSSProp(/* BorderTopRightRadius */ 79, [0]), new CSSProp(/* BorderBottomRightRadius */ 45, [0]), new CSSProp(/* BorderRightWidth */ 73, ["0.5"]), new CSSProp(/* WhiteSpace */ 392, ["nowrap"])])), 1)];

export const scrollbarClkCycleLeftStyle = ["style", keyValueList(append(clkCycleBut(softScrollBarWidth), ofArray([new CSSProp(/* BorderTopLeftRadius */ 78, ["4px"]), new CSSProp(/* BorderBottomLeftRadius */ 44, ["4px"]), new CSSProp(/* BorderTopRightRadius */ 79, [0]), new CSSProp(/* BorderBottomRightRadius */ 45, [0]), new CSSProp(/* BorderRightWidth */ 73, ["0.5"])])), 1)];

export const clkCycleRightStyle = ["style", keyValueList(append(clkCycleBut(rowHeight), ofArray([new CSSProp(/* BorderTopLeftRadius */ 78, [0]), new CSSProp(/* BorderBottomLeftRadius */ 44, [0]), new CSSProp(/* BorderTopRightRadius */ 79, ["4px"]), new CSSProp(/* BorderBottomRightRadius */ 45, ["4px"]), new CSSProp(/* BorderLeftWidth */ 68, ["0.5"]), new CSSProp(/* WhiteSpace */ 392, ["nowrap"])])), 1)];

export const scrollbarClkCycleRightStyle = ["style", keyValueList(append(clkCycleBut(softScrollBarWidth), ofArray([new CSSProp(/* BorderTopLeftRadius */ 78, [0]), new CSSProp(/* BorderBottomLeftRadius */ 44, [0]), new CSSProp(/* BorderTopRightRadius */ 79, ["4px"]), new CSSProp(/* BorderBottomRightRadius */ 45, ["4px"]), new CSSProp(/* BorderLeftWidth */ 68, ["0.5"])])), 1)];

/**
 * Style for Bulma level element in name row
 */
export function nameRowLevelStyle(isHovered) {
    return ["style", keyValueList(append(toList(delay(() => append_1(singleton_1(new CSSProp(/* Height */ 189, [rowHeight])), delay(() => (isHovered ? singleton_1(new CSSProp(/* Cursor */ 123, ["grab"])) : empty_1()))))), namesColBackground(isHovered ? "hsl(0, 0%, 96%)" : "transparent", [1, separatorColour, false])), 1)];
}

/**
 * Style for name label
 */
export function nameLabelStyle(isHovered) {
    return ["style", keyValueList(toList(delay(() => (isHovered ? singleton_1(new CSSProp(/* Cursor */ 123, ["grab"])) : empty_1()))), 1)];
}

/**
 * Style for value label
 */
export function valueLabelStyle(ws) {
    return ["style", {
        height: rowHeight,
        borderBottom: "1px solid rgb(219,219,219)",
        paddingLeft: labelPadding,
        paddingTop: valueTopPadding(ws),
        fontFamily: valueColumnFontFamily,
        fontSize: ws.WSConfig.FontSize,
        fontWeight: ws.WSConfig.FontWeight,
    }];
}

/**
 * Prop for Level.left in name row.
 */
export function nameRowLevelLeftProps(visibility) {
    return singleton(["style", {
        position: "sticky",
        left: 0,
        visibility: visibility,
    }]);
}

/**
 * Eye, drawn to the size of the box it is given. Marks the button which shows a waveform's
 * component on the schematic.
 */
export function eyeSvg(colour, size) {
    const children_2 = [react.createElement("path", {
        fill: colour,
        d: "M288 96c-93 0-171 56-224 160 53 104 131 160 224 160s171-56 224-160c-53-104-131-160-224-160zm0\r\n               266c-58 0-106-47-106-106s48-106 106-106 106 47 106 106-48 106-106 106zm0-170c-35 0-64 29-64 64s29\r\n               64 64 64 64-29 64-64-29-64-64-64z",
    })];
    return react.createElement("svg", {
        viewBox: "0 0 576 512",
        height: size,
        width: size,
    }, ...children_2);
}

/**
 * Style of the button which shows a waveform's component on the schematic. It sits in a slot of
 * its own at the outer edge of the name row, beyond the delete icon, so that it reads as an extra
 * beside the label rather than as one of the label's own controls. Green when the component cannot
 * be seen and grey when it is already in front of the user; shown only while the row is hovered.
 */
export function viewSymbolButtonStyle(isInView) {
    return ["style", {
        width: viewSymbolWidth,
        height: viewSymbolWidth,
        minWidth: viewSymbolWidth,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "4px",
        marginRight: "2px",
        backgroundColor: isInView ? "hsl(0, 0%, 71%)" : "hsl(141, 53%, 53%)",
        cursor: "pointer",
    }];
}

/**
 * Calculate the necessary with of the naes column based on the longest name.
 */
export function calcNamesColWidth(ws) {
    let refFont, d;
    return ((((refFont = (new Text$(defaultText.TextAnchor, "10px", defaultText.FontWeight, columnFontFamily, defaultText.Fill, defaultText.UserSelect, defaultText.DominantBaseline)), ~~((d = max_1(map((txt) => ((ws.WSConfig.FontSize * getTextWidthInPixels(refFont, txt)) / 10), cons("Dummy", map((wave) => wave.ViewerDisplayName, selectedWaves(ws)))), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }), Math.ceil(d))))) + deleteSymbolWidth) + viewSymbolWidth) | 0;
}

export const waveSimColumn = ofArray([new CSSProp(/* BorderTop */ 76, [borderProperties]), new CSSProp(/* Height */ 189, ["100%"]), new CSSProp(/* Width */ 395, ["100%"]), new CSSProp(/* Display */ 125, ["grid"]), new CSSProp(/* GridAutoRows */ 172, [rowHeight]), new CSSProp(/* FontFamily */ 149, [valueColumnFontFamily]), new CSSProp(/* OverflowX */ 271, ["auto"]), new CSSProp(/* WhiteSpace */ 392, ["nowrap"]), new CSSProp(/* LineHeight */ 213, ["25px"])]);

/**
 * Style properties for names column
 */
export function namesColumnStyle(ws) {
    return ["style", keyValueList(append(waveSimColumn, append(namesColBackground(namesValuesColumnColor, [2, separatorColour, true]), ofArray([new CSSProp(/* BorderTop */ 76, ["none"]), new CSSProp(/* Width */ 395, [calcNamesColWidth(ws)]), new CSSProp(/* Float */ 144, ["left"]), new CSSProp(/* BackgroundColor */ 21, [namesValuesColumnColor]), new CSSProp(/* FontSize */ 153, [ws.WSConfig.FontSize]), new CSSProp(/* FontWeight */ 165, [ws.WSConfig.FontWeight]), new CSSProp(/* BorderRight */ 70, [borderProperties]), new CSSProp(/* GridColumnStart */ 176, [1]), new CSSProp(/* OverflowX */ 271, ["clip"]), new CSSProp(/* TextAlign */ 325, ["right"])]))), 1)];
}

/**
 * Props for names column
 */
export function namesColumnProps(ws) {
    return ofArray([new HTMLAttr(/* Id */ 99, ["namesColumn"]), namesColumnStyle(ws)]);
}

export function valueColumnTextStyle(wsModel) {
    return new Text$(defaultText.TextAnchor, `${wsModel.WSConfig.FontSize}px`, int32ToString(wsModel.WSConfig.FontWeight), valueColumnFontFamily, defaultText.Fill, defaultText.UserSelect, defaultText.DominantBaseline);
}

export function valuesColumnSize(wsModel) {
    const colText = valueColumnTextStyle(wsModel);
    const widthOfOneChar = getTextWidthInPixels(colText, "0");
    const maxValueBusWidth = max_1(cons(1, map((wave) => (wave.Width | 0), selectedWaves(wsModel))), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }) | 0;
    let tupledArg_1;
    const tupledArg = unzip(map((num) => {
        let patternInput;
        const matchValue = wsModel.Radix;
        patternInput = ((matchValue.tag === 0) ? [op_Subtraction(op_LeftShift(fromOne(), num), fromOne()), 10] : ((matchValue.tag === 1) ? [op_LeftShift(fromOne(), num), 2 * widthOfOneChar] : ((matchValue.tag === 3) ? [op_LeftShift(fromOne(), num - 1), 3 * widthOfOneChar] : [op_LeftShift(fromOne(), num - 1), 10])));
        const v = fastDataToPaddedString(valueColumnMaxChars, wsModel.Radix, new FastData(new FastBits(/* BigWord */ 1, [patternInput[0]]), num + 3));
        return [patternInput[1] + (1.05 * getTextWidthInPixels(colText, v)), v.length + 2];
    }, ofArray([maxValueBusWidth, min(maxValueBusWidth, Constants_maxBinaryDisplayWidth)])));
    tupledArg_1 = [max_1(tupledArg[0], {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }), max_1(tupledArg[1], {
        Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
    })];
    return [~~tupledArg_1[0], tupledArg_1[1]];
}

/**
 * Style properties for values column
 */
export function valuesColumnStyle(ws, colWidth) {
    return ["style", keyValueList(append(waveSimColumn, ofArray([new CSSProp(/* FontSize */ 153, [ws.WSConfig.FontSize]), new CSSProp(/* FontWeight */ 165, [ws.WSConfig.FontWeight]), new CSSProp(/* MinWidth */ 253, [colWidth]), new CSSProp(/* Float */ 144, ["left"]), new CSSProp(/* BorderLeft */ 65, [borderProperties]), new CSSProp(/* OverflowX */ 271, ["auto"]), new CSSProp(/* BackgroundColor */ 21, [namesValuesColumnColor]), new CSSProp(/* Opacity */ 261, [1]), new CSSProp(/* GridColumnStart */ 176, [3])])), 1)];
}

export const waveformColumnStyle = ["style", {
    gridColumnStart: 2,
    display: "grid",
}];

/**
 * Style for rows in waveforms column
 */
export function waveRowsStyle(wsModel) {
    return ["style", {
        height: "100%",
        overflowX: "hidden",
        display: "grid",
        gridAutoRows: rowHeight,
        borderTop: borderProperties,
        width: wsModel.WaveformColumnWidth,
        gridColumnStart: 1,
        gridRowStart: 1,
        backgroundImage: (`repeating-linear-gradient(to bottom, transparent 0px, transparent ${rowHeight}px, `) + (`${rowBandColor} ${rowHeight}px, ${rowBandColor} ${2 * rowHeight}px)`),
        backgroundSize: `100% ${(length(selectedWaves(wsModel)) + 1) * rowHeight}px`,
        backgroundRepeat: "no-repeat",
    }];
}

export const viewWaveSimStyle = ["style", {
    marginLeft: leftMargin,
    marginRight: rightMargin,
    marginTop: "5px",
}];

export const showWaveformsAndRamStyle = ["style", {
    width: "100%",
    height: `calc(100vh - ${topHalfHeight}px)`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden hidden",
}];

export const waveformScrollerStyle = ["style", {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden auto",
}];

/**
 * The RAM tables within that pane: at most half of it, less if they need less, scrolling within
 * whatever they get. Nothing at all when no RAM is selected, which is what leaves the waveforms
 * the whole pane.
 */
export function ramTablesStyle(sharingWithWaveforms) {
    return ["style", {
        width: "100%",
        flex: "0 0 auto",
        maxHeight: sharingWithWaveforms ? "50%" : "100%",
        minHeight: 0,
        overflow: "hidden auto",
    }];
}

export const showWaveformsStyle = ["style", {
    width: "100%",
    display: "grid",
    columnCount: 3,
    gridAutoFlow: "column",
    gridAutoColumns: "min-content",
    overflowX: "visible",
}];

/**
 * The height the waveform table wants: one row per waveform DRAWN, which is a selected wave that
 * WaveDetails still holds, plus the row of clock cycle numbers above them.
 */
export function calcWaveformHeight(wsModel) {
    return ((rowHeight * length(selectedWaves(wsModel))) + (0.6 * viewBoxHeight)) + 20;
}

export function calcWaveformAndScrollBarHeight(wsModel) {
    return (calcWaveformHeight(wsModel) + 100) + scrollBarWidth;
}

/**
 * Props for text in clock cycle row
 */
export function clkCycleText(m, i) {
    const props = ofArray([new SVGAttr(/* FontSize */ 9, ["12px"]), new SVGAttr(/* TextAnchor */ 37, ["middle"]), new SVGAttr(/* X */ 44, [singleWaveWidth(m) * ((i - m.StartCycle) + 0.5)]), new SVGAttr(/* Y */ 57, [0.6 * viewBoxHeight])]);
    if (i === m.CursorDisplayCycle) {
        return append(singleton(["fontWeight", "bold"]), props);
    }
    else {
        return props;
    }
}

export const clkCycleSVGStyle = ["style", {
    display: "block",
    borderBottom: borderProperties,
}];

/**
 * Props for waveform column rows
 */
export function waveformColumnRowProps(m) {
    return ofArray([new SVGAttr(/* Height */ 14, [rowHeight]), new SVGAttr(/* Width */ 41, [viewBoxWidth(m)]), new SVGAttr(/* ViewBox */ 40, [(((viewBoxMinX(m) + " 0 ") + viewBoxWidth(m)) + " ") + viewBoxHeight.toString()]), new SVGAttr(/* PreserveAspectRatio */ 23, ["none"])]);
}

/**
 * Props for row of clock cycle numbers
 */
export function clkCycleNumberRowProps(m) {
    return append(waveformColumnRowProps(m), singleton(clkCycleSVGStyle));
}

export const waveRowSVGStyle = ["style", {
    display: "block",
    borderBottom: "1px solid rgb(219,219,219)",
}];

/**
 * Props for each row in waveform column
 */
export function waveRowProps(m) {
    return append(waveformColumnRowProps(m), singleton(waveRowSVGStyle));
}

export const clkLineStyle = ["style", {
    stroke: "rgb(200,200,200)",
    opacity: 0.5,
    strokeWidth: clkLineWidth,
}];

/**
 * Grid lines separating clock cycles.
 * 
 * Placed relative to the FIRST SHOWN cycle, like the cursor highlight beside them, because the
 * SVG they are drawn in starts at x = 0 whatever the view is scrolled to (`viewBoxMinX`). Drawing
 * them at the absolute cycle number instead put them StartCycle cycles to the right of where they
 * belong - off the end of the waveforms, and only visible at all because that SVG does not clip
 * (it must not, or a tooltip could not run out over the columns beside it). Scrolled to cycle 0
 * they were right, which is why this survived: what it looked like further along was a grid that
 * came and went with the scroll position.
 */
export function backgroundSVG(wsModel, count) {
    return map((x_1) => {
        const x = x_1 * singleWaveWidth(wsModel);
        const props = [clkLineStyle, new SVGAttr(/* X1 */ 42, [x]), new SVGAttr(/* Y1 */ 55, [0]), new SVGAttr(/* X2 */ 43, [x]), new SVGAttr(/* Y2 */ 56, [viewBoxHeight * (count + 1)])];
        return react.createElement("line", keyValueList(props, 1));
    }, toList(rangeDouble(1, 1, wsModel.ShownCycles)));
}

/**
 * Change Tooltip SVG element based on mouse position.
 */
export function setWaveToolTip(m, ev) {
    let drawn_3, window$, this$;
    const svgHighlight = document.getElementById("ClkCycleHighlight");
    const bcr = svgHighlight.getBoundingClientRect();
    const sample = ~~((ev.clientX - bcr.left) / singleWaveWidth(m)) | 0;
    const waveNum = (~~((ev.clientY - bcr.top) / rowHeight) - 1) | 0;
    let drawnHere;
    const option_3 = tryItem(waveNum, selectedWaves(m));
    if (option_3 != null) {
        const wave = option_3;
        const option_1 = tryDrawn(wave.DriverIndex);
        drawnHere = ((option_1 != null) ? [wave, option_1] : undefined);
    }
    else {
        drawnHere = undefined;
    }
    const numValText = (drawnHere != null) ? getWaveToolTip(sample, drawnHere[1], drawnHere[0], m) : "";
    const ttXPos = sample * singleWaveWidth(m);
    const ttYPos = (waveNum * rowHeight) + (16 / 2);
    changeToolTip("waveTip", (drawnHere != null) ? (((drawnHere[1], numValText !== "")) ? ((drawn_3 = drawnHere[1], (window$ = drawn_3.Spec.Window, `Cycle:${((this$ = window$, this$.StartSample * this$.Multiplier)) + (sample * window$.Multiplier)}. ${numValText}`))) : "") : "", ttXPos, ttYPos, (m.ShownCycles * singleWaveWidth(m)) + valuesColumnSize(m)[0], numValText !== "");
}

/**
 * Controls the background highlighting of which clock cycle is selected
 */
export function cursorCycleHighlightSVG(m, dispatch) {
    let props;
    const count = length(selectedWaves(m)) | 0;
    const props_2 = [new SVGAttr(/* Fill */ 6, [cursorColumnColor]), new SVGAttr(/* Opacity */ 19, [1]), ["style", keyValueList([new CSSProp(/* GridColumnStart */ 176, [1]), new CSSProp(/* GridRowStart */ 183, [1]), new CSSProp(/* ZIndex */ 404, [33]), CSSProp_Overflow_27AED5E3("visible")], 1)], new SVGAttr(/* Height */ 14, [int32ToString((count + 1) * rowHeight) + "px"]), new SVGAttr(/* Width */ 41, [viewBoxWidth(m)]), new SVGAttr(/* ViewBox */ 40, [(((viewBoxMinX(m) + " 0 ") + viewBoxWidth(m)) + " ") + (viewBoxHeight * (count + 1)).toString()]), new HTMLAttr(/* Id */ 99, ["ClkCycleHighlight"]), new DOMAttr(/* OnMouseMove */ 54, [(ev) => {
        setWaveToolTip(m, ev);
    }]), new DOMAttr(/* OnClick */ 40, [(ev_1) => {
        const svgEl = document.getElementById("ClkCycleHighlight");
        const bcr = svgEl.getBoundingClientRect();
        bcr.width / m.ShownCycles;
        const cycle = (~~((ev_1.clientX - bcr.left) / singleWaveWidth(m)) + m.StartCycle) | 0;
        dispatch(new Msg(/* UpdateWSModel */ 13, [(m_1) => (new WaveSimModel(m_1.DefaultCursor, m_1.WSConfig, m_1.WSConfigDialog, m_1.State, m_1.WaveDetails, m_1.SelectedWaves, m_1.DefaultSelectionPending, m_1.FetchFailedAtMs, m_1.StartCycle, m_1.ShownCycles, m_1.SamplingZoom, cycle, cycle * m_1.SamplingZoom, m_1.ClkCycleBoxIsEmpty, m_1.Radix, m_1.WaveformColumnWidth, m_1.WaveModalActive, m_1.RamModalActive, m_1.PortSelectComp, m_1.RamComps, m_1.SelectedRams, m_1.RamStartLocation, m_1.RamRows, m_1.ShowSheetDetail, m_1.SelectedSheetInstance, m_1.ShowGroupDetail, m_1.HoveredLabel, m_1.DraggedIndex, m_1.PrevSelectedWaves, m_1.ScrollbarTbWidth, m_1.ScrollbarTbPos, m_1.ScrollbarTbOffset, m_1.ScrollbarBkgWidth, m_1.ScrollbarBkgRepCycs, m_1.ScrollbarQueueIsEmpty, m_1.SheetSearchString, m_1.ComponentSearchString, m_1.PortSearchString, m_1.ShowOnlySelected))]));
    }])];
    const children_2 = concat([singleton((props = [new SVGAttr(/* Width */ 41, [singleWaveWidth(m)]), new SVGAttr(/* Height */ 14, ["100%"]), new SVGAttr(/* Opacity */ 19, [0.2]), new SVGAttr(/* X */ 44, [(m.CursorDisplayCycle - m.StartCycle) * singleWaveWidth(m)])], react.createElement("rect", keyValueList(props, 1)))), backgroundSVG(m, count), singleton(svgWaveToolTip(m))]);
    return react.createElement("svg", keyValueList(props_2, 1), ...children_2);
}

export const radixTabProps = singleton(["style", {
    width: "35px",
    height: rowHeight,
}]);

export const radixTabAStyle = ["style", {
    padding: "0 0 0 0",
    height: rowHeight,
}];

export const radixTabsStyle = ["style", {
    height: rowHeight,
    fontSize: "80%",
    overflowX: "clip",
    display: "inline",
}];

/**
 * Style of polyline used to draw waveforms
 */
export function wavePolylineStyle(points) {
    return ofArray([new SVGAttr(/* Stroke */ 30, ["blue"]), new SVGAttr(/* Fill */ 6, ["none"]), new SVGAttr(/* StrokeWidth */ 36, [lineThickness]), new SVGAttr(/* Points */ 22, [pointsToString(points)])]);
}

export function wavePolyfillStyle(points) {
    return ofArray([new SVGAttr(/* Stroke */ 30, ["none"]), new SVGAttr(/* Fill */ 6, ["lightgrey"]), new SVGAttr(/* StrokeWidth */ 36, [lineThickness + 2]), new SVGAttr(/* Points */ 22, [pointsToString(points)])]);
}

export const topHalfStyle = ["style", {
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 10000,
    marginRight: waveTableRightGap - topRowButtonMargin,
    paddingLeft: viewSymbolWidth - topRowButtonMargin,
}];

/**
 * Open or close nodes of the wave selector's hierarchy, each named by its path of design-time
 * sheet names.
 * 
 * Opening one closes any other node of the same sheet. A sheet that several routes reach appears
 * in several places, and showing more than one of them at once is the multiplying that collapsing
 * the hierarchy is there to prevent. Where only one route reaches a sheet this is vacuous - there
 * is only one such node - so the rule needs no test for which kind of sheet it is.
 */
export function setWaveSheetSelectionOpen(wsModel, subSheets, show) {
    let sheetsOpening;
    const setChange = ofList(subSheets, {
        Compare: (x, y) => (compare(x, y) | 0),
    });
    return new WaveSimModel(wsModel.DefaultCursor, wsModel.WSConfig, wsModel.WSConfigDialog, wsModel.State, wsModel.WaveDetails, wsModel.SelectedWaves, wsModel.DefaultSelectionPending, wsModel.FetchFailedAtMs, wsModel.StartCycle, wsModel.ShownCycles, wsModel.SamplingZoom, wsModel.CursorDisplayCycle, wsModel.CursorExactClkCycle, wsModel.ClkCycleBoxIsEmpty, wsModel.Radix, wsModel.WaveformColumnWidth, wsModel.WaveModalActive, wsModel.RamModalActive, wsModel.PortSelectComp, wsModel.RamComps, wsModel.SelectedRams, wsModel.RamStartLocation, wsModel.RamRows, show ? ((sheetsOpening = ofList(choose(tryLast, subSheets), {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }), union(setChange, filter((openKey) => {
        const matchValue = tryLast(openKey);
        if (matchValue == null) {
            return true;
        }
        else {
            return !contains(matchValue, sheetsOpening);
        }
    }, wsModel.ShowSheetDetail)))) : difference(wsModel.ShowSheetDetail, setChange), wsModel.SelectedSheetInstance, wsModel.ShowGroupDetail, wsModel.HoveredLabel, wsModel.DraggedIndex, wsModel.PrevSelectedWaves, wsModel.ScrollbarTbWidth, wsModel.ScrollbarTbPos, wsModel.ScrollbarTbOffset, wsModel.ScrollbarBkgWidth, wsModel.ScrollbarBkgRepCycs, wsModel.ScrollbarQueueIsEmpty, wsModel.SheetSearchString, wsModel.ComponentSearchString, wsModel.PortSearchString, wsModel.ShowOnlySelected);
}

/**
 * Sets or clears a subset of ShowGroupDetail
 */
export function setWaveGroupSelectionOpen(wsModel, grps, show) {
    const grpSet = ofList(grps, {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    });
    return new WaveSimModel(wsModel.DefaultCursor, wsModel.WSConfig, wsModel.WSConfigDialog, wsModel.State, wsModel.WaveDetails, wsModel.SelectedWaves, wsModel.DefaultSelectionPending, wsModel.FetchFailedAtMs, wsModel.StartCycle, wsModel.ShownCycles, wsModel.SamplingZoom, wsModel.CursorDisplayCycle, wsModel.CursorExactClkCycle, wsModel.ClkCycleBoxIsEmpty, wsModel.Radix, wsModel.WaveformColumnWidth, wsModel.WaveModalActive, wsModel.RamModalActive, wsModel.PortSelectComp, wsModel.RamComps, wsModel.SelectedRams, wsModel.RamStartLocation, wsModel.RamRows, wsModel.ShowSheetDetail, wsModel.SelectedSheetInstance, show ? union(grpSet, wsModel.ShowGroupDetail) : difference(wsModel.ShowGroupDetail, grpSet), wsModel.HoveredLabel, wsModel.DraggedIndex, wsModel.PrevSelectedWaves, wsModel.ScrollbarTbWidth, wsModel.ScrollbarTbPos, wsModel.ScrollbarTbOffset, wsModel.ScrollbarBkgWidth, wsModel.ScrollbarBkgRepCycs, wsModel.ScrollbarQueueIsEmpty, wsModel.SheetSearchString, wsModel.ComponentSearchString, wsModel.PortSearchString, wsModel.ShowOnlySelected);
}

export function setSelectionOpen(cBox, show, wsModel) {
    if (cBox.tag === 1) {
        return setWaveSheetSelectionOpen(wsModel, singleton(cBox.fields[0]), show);
    }
    else {
        return setWaveGroupSelectionOpen(wsModel, singleton([cBox.fields[0], cBox.fields[1]]), show);
    }
}

/**
 * Props for HTML Summary element
 * <param name="isSummary">True if this is a summary element, false if it is a details element.</param>
 * isSummary is used to determine if the click handler is used.
 */
export function summaryProps(isSummary, cBox, ws, dispatch) {
    return toList(delay(() => {
        const patternInput = (cBox.tag === 0) ? ["14px", "bold"] : ["20px", "bold"];
        return append_1(singleton_1(["style", {
            fontSize: patternInput[0],
            fontWeight: patternInput[1],
        }]), delay(() => singleton_1(new DOMAttr(/* OnClick */ 40, [(arg) => {
            let show_1;
            if (isSummary) {
                dispatch(new Msg(/* UpdateWSModel */ 13, [(show_1 = !((cBox.tag === 0) ? contains([cBox.fields[0], cBox.fields[1]], ws.ShowGroupDetail) : contains(cBox.fields[0], ws.ShowSheetDetail)), (wsModel) => setSelectionOpen(cBox, show_1, wsModel))]));
            }
        }]))));
    }));
}

/**
 * Props for HTML Details element
 */
export function detailsProps(showDetails, cBox, ws, dispatch) {
    return singleton(new HTMLAttr(/* Open */ 125, [((cBox.tag === 0) ? contains([cBox.fields[0], cBox.fields[1]], ws.ShowGroupDetail) : contains(cBox.fields[0], ws.ShowSheetDetail)) ? true : showDetails]));
}

